import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { User, UserModel } from '@app/shared/models/user';
import { UserManagementReport } from '@app/shared/models/users-management';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ApiClientConfig } from '../api-client.config';
import { RelationshipCountsRes } from '../verification-requests/api.verification-request.models';
import {
  ModerationAreas,
  ModerationAreasResponse,
  ModeratorRole,
  ModeratorRoleResponse,
  PermissionsRoleResponse,
  UserFrameResponse,
  UserInfoResponse,
  UserManagementResponse
} from './api.user.model';

export class UserApi {
  private apiUrl = environment.API_URL + '/user';

  constructor(
    public readonly http: HttpClient,
    public config: ApiClientConfig
  ) {}

  getUserById(id: string): Observable<UserInfoResponse> {
    return this.http.get<any>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserInfo(userId: string): Observable<any> {
    return this.http.get<any>(this.apiUrl + '/users/info/' + userId).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getTotalsUser(roles: any, statuses: any, keyword: string) {
    return this.http.get<UserModel[]>(this.apiUrl + '/users').pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        const users = res.data;
        res.data = users.filter((item: any) => {
          if (keyword.trim() !== '') {
            return (
              (item.last_name?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.first_name?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.email?.toLowerCase().includes(keyword.toLowerCase()) ||
                item.register_date?.toLowerCase().includes(keyword.toLowerCase())) &&
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) &&
              (statuses && statuses.length > 0 ? statuses.includes(item.status) : true)
            );
          } else {
            return (
              (roles && roles.length > 0
                ? roles.some((element: any) => item.roles.includes(element))
                : true) && (statuses && statuses.length > 0 ? statuses.includes(item.status) : true)
            );
          }
        });
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  updateUser(userId: string, userDetail: Partial<any>): Observable<any> {
    return this.http.put<any>(this.apiUrl + '/users/' + userId, userDetail).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  delete(id: string) {
    return this.http.delete<User>(this.apiUrl + '/users/' + id).pipe(
      timeout(this.config.responseTimeout),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserManagementReport(): Observable<UserManagementReport> {
    return this.http.get<any>(this.apiUrl + '/admin/user-management-report').pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        if (res.success) {
          return res.data;
        }
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getUserManagement(
    pageNum: number,
    pageSize: number,
    status: string
  ): Observable<UserManagementResponse> {
    return this.http
      .get<UserManagementResponse>(
        this.apiUrl + `/admin/search?pageNum=${pageNum}&pageSize=${pageSize}&status=${status}`
      )
      .pipe(
        timeout(this.config.responseTimeout),
        map((res: UserManagementResponse) => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getToken(): string | null {
    const auth = localStorage.getItem(environment.TOKEN);
    const authValue = auth ?? '';
    if (authValue !== '') {
      try {
        return JSON.parse(authValue).accessToken; // Parse the JSON string to an object
      } catch (error) {
        console.error('Error parsing authentication token:', error);
        return null;
      }
    } else {
      return null;
    }
  }

  getModeratorRole(): Observable<ModeratorRole[]> {
    const token = this.getToken();
    const headers = new HttpHeaders().set('Authorization', 'Bearer ' + token);
    return this.http
      .get<ModeratorRoleResponse>(`${this.apiUrl}/admin/roles/module`, { headers })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res.data;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getModerationAreas(textSearch: string, isOtaOnly = false): Observable<ModerationAreas[]> {
    let params = new HttpParams();
    if (textSearch) {
      params = params.set('textSearch', textSearch);
    }
    if (isOtaOnly) {
      params = params.set('is_get_ota', true);
    }
    return this.http
      .get<ModerationAreasResponse>(`${this.apiUrl}/admin/moderation-areas`, { params })
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res.data;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getTokenToLoadIframe(): Observable<string> {
    return this.http.get<UserFrameResponse>(`${this.apiUrl}/admin/approval-token`).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res.data;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getPermissionsTree(nodeId?: string) {
    let url = `${this.apiUrl}/admin-permissions`;
    if (nodeId) url = `${url}?nodeId=${nodeId}`;
    return this.http.get<PermissionsRoleResponse>(url).pipe(
      timeout(30000),
      map((res: PermissionsRoleResponse) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getPermissionCodeList() {
    return this.http.get<any>(`${this.apiUrl}/admin-permissions/hub/MODERATION/permission`).pipe(
      timeout(this.config.responseTimeout),
      map((res: any) => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getRelationshipCounts(usernameOrId: string): Observable<RelationshipCountsRes> {
    return this.http
      .get<RelationshipCountsRes>(`${this.apiUrl}/${usernameOrId}/relationship-counts`)
      .pipe(
        timeout(this.config.responseTimeout),
        map(res => {
          return res;
        }),
        catchError(error => {
          throw error.error;
        })
      );
  }

  getCensorManagementList(
    pageNum: number,
    pageSize: number,
    nodeId: string,
    countryCode: string,
    searchText?: string,
    module?: string,
    status?: string,
    role?: string
  ): Observable<any> {
    let url = `${this.apiUrl}/admin/censor-management?pageNum=${pageNum}&pageSize=${pageSize}&nodeId=${nodeId}&countryCode=${countryCode}`;
    if (searchText) url = `${url}&textSearch=${searchText}`;
    if (module) url = `${url}&type=${module}`;
    if (status) url = `${url}&status=${status}`;
    if (role) url = `${url}&role=${role}`;
    return this.http.get<any>(url).pipe(
      timeout(60000),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getDetailCensorManagement(
    nodeId: string,
    countryCode: string,
    module?: string,
    status?: string,
    role?: string
  ): Observable<any> {
    let url = `${this.apiUrl}/admin/detail-censor-management?&nodeId=${nodeId}&countryCode=${countryCode}`;
    if (module) url = `${url}&type=${module}`;
    if (status) url = `${url}&status=${status}`;
    if (role) url = `${url}&role=${role}`;
    return this.http.get<any>(url).pipe(
      timeout(60000),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getCensorVerificationPerformanceDetail(
    userId: string,
    objectType: string,
    rootId: string,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    let url = `${environment.API_URL}/verification/admin/verification/performance/detail/${userId}?type=${objectType}&root=${rootId}`;
    if (fromDate) url = `${url}&fromDate=${fromDate}`;
    if (toDate) url = `${url}&toDate=${toDate}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getCensorPerformanceDetail(
    userId: string,
    objectType: string,
    rootId: string,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    let url = `${environment.API_URL}/social-moderator/moderate-contents-history/performance/detail/${userId}?objectType=${objectType}&root=${rootId}`;
    if (fromDate) url = `${url}&fromDate=${fromDate}`;
    if (toDate) url = `${url}&toDate=${toDate}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }

  getCensorPerformance(
    userId: string,
    rootId: string,
    fromDate?: string,
    toDate?: string
  ): Observable<any> {
    let url = `${environment.API_URL}/social-moderator/moderate-contents-history/performance/${userId}?root=${rootId}`;
    if (fromDate) url = `${url}&fromDate=${fromDate}`;
    if (toDate) url = `${url}&toDate=${toDate}`;
    return this.http.get<any>(url).pipe(
      timeout(this.config.responseTimeout),
      map(res => {
        return res;
      }),
      catchError(error => {
        throw error.error;
      })
    );
  }
}
